import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchFacets } from '@app/src/api/fetchHooks'
import { usePageBreadcrumbs, useTabCrumb } from '@app/src/components/Navigation/Breadcrumbs/usePageBreadcrumbs'
import type { TabsProps } from '@app/src/components/Tabs'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { ProviderLinkStatus } from '@app/src/types/organizations'
import React from 'react'
import { useIntl } from 'react-intl'
import { generatePath, Redirect, Route, Switch, useParams, useRouteMatch } from 'react-router'
import AllProvidersScene from './AllProvidersScene'
import ConnectedProvidersScene from './ConnectedProvidersScene'
import MissingContactProvidersScene from './MissingContactProvidersScene'
import NotConnectedProvidersScene from './NotConnectedProvidersScene'

interface ProvidersCollectionProps {
  relatedContentFilter?: FilterGroup[]
}

export enum ProviderTabs {
  All = 'all',
  Connected = 'connected',
  NotConnected = 'not-connected',
  MissingContacts = 'missing-contacts',
}

interface ProvidersCollectionSceneParams {
  view: string
}

export const allowedFilters = [
  'id',
  'name',
  'country.id',
  'createdAt',
  'providerOwners.userId',
  'providerOwners.user',
  'providerOwners.userId',
  'providerOwners.user',
  'websiteAddress',
  'vatNumber',
  'registrationNumber',
  'linkStatus',
  'categoryOptions.id',
  'activityStatus',
  'finalRiskRating',
  'providerTopicalRisks.riskStatus',
  'priority',
  'providerApprovalStatus',
  'supplierUsage',
  'tier',
  'customId',
  'organization.id',
  'mappingNodes.product.productStatus',
  'mappingNodes.actorType.id',
  'mappingNodes.tier',
  'organizationId',
  'spends.periodName',
  'spends.spendClassification',
  'assessments.periodName',
  'assessments.totalLevel',
  'assessments.section1Level',
  'assessments.section2Level',
  'assessments.section3Level',
  'assessments.section4Level',
  'assessments.section5Level',
  'assessments.assessmentTemplate.name',
  'assessments.assessmentTemplate.id',
  'assessments.assessmentTemplate.sections.name',
  'industry.id',
]

const ProvidersCollection: React.FC<ProvidersCollectionProps> = ({ relatedContentFilter = [] }) => {
  const { formatMessage } = useIntl()
  const { view } = useParams<ProvidersCollectionSceneParams>()
  // Base path of whichever route mounted this scene, so its tabs/redirect/routes work under both
  // `/providers` (rendered by the dispatcher when the flag is off) and `/providers-existing`.
  const { path: basePath } = useRouteMatch()
  const { scope } = useAuthentication()
  const solution = scope.solution
  const { count: referralContactCount } = useFetchFacets({
    key: FetchKey.ProviderReferralContactFacets,
    endpoint: endpoints.providersWithFacets,
    filter: [
      {
        name: 'linkStatus',
        filters: [
          {
            value: ProviderLinkStatus.Created,
            operator: Operators.EqualTo,
          },
        ],
      },
      {
        name: 'referralContact.email',
        filters: [
          {
            operator: Operators.IsNull,
          },
        ],
      },
    ],
    facetsParam: [{ name: 'id' }],
  })

  const actionTabs: TabsProps['tabs'] = [
    {
      type: ProviderTabs.All,
      url: generatePath(basePath, { view: ProviderTabs.All }),
      label: formatMessage({ id: 'resourceExplorer.provider.all' }),
    },
    {
      type: ProviderTabs.Connected,
      url: generatePath(basePath, { view: ProviderTabs.Connected }),
      label: formatMessage({ id: 'resourceExplorer.provider.connected' }),
    },
    {
      type: ProviderTabs.NotConnected,
      url: generatePath(basePath, { view: ProviderTabs.NotConnected }),
      label: formatMessage({ id: 'resourceExplorer.provider.notConnected' }),
    },
    {
      type: ProviderTabs.MissingContacts,
      url: generatePath(basePath, { view: ProviderTabs.MissingContacts }),
      label: formatMessage({ id: 'resourceExplorer.provider.missingContact' }),
      hidden: !referralContactCount,
    },
  ]

  const providersCrumb = { label: formatMessage({ id: `navbar.${solution}` }) }
  const companiesCrumb = {
    label: formatMessage({ id: 'navbar.companies' }),
    to: generatePath(basePath, { view: ProviderTabs.All }),
  }
  const tabCrumb = useTabCrumb(actionTabs, view)
  usePageBreadcrumbs(tabCrumb ? [providersCrumb, companiesCrumb, tabCrumb] : [providersCrumb, companiesCrumb])

  if (!view) {
    return (
      <Redirect
        to={generatePath(basePath, {
          view: ProviderTabs.All,
        })}
      />
    )
  }

  return (
    <Switch>
      <Route path={generatePath(basePath, { view: ProviderTabs.All })}>
        <AllProvidersScene
          tabs={{ actionTabs, activeTabParam: view }}
          allowedFilters={allowedFilters}
          relatedContentFilter={relatedContentFilter}
        />
      </Route>
      <Route path={generatePath(basePath, { view: ProviderTabs.Connected })}>
        <ConnectedProvidersScene
          tabs={{ actionTabs, activeTabParam: view }}
          allowedFilters={allowedFilters}
          relatedContentFilter={relatedContentFilter}
        />
      </Route>
      <Route path={generatePath(basePath, { view: ProviderTabs.NotConnected })}>
        <NotConnectedProvidersScene
          tabs={{ actionTabs, activeTabParam: view }}
          allowedFilters={allowedFilters}
          relatedContentFilter={relatedContentFilter}
        />
      </Route>
      <Route path={generatePath(basePath, { view: ProviderTabs.MissingContacts })}>
        <MissingContactProvidersScene
          tabs={{ actionTabs, activeTabParam: view }}
          allowedFilters={allowedFilters}
          relatedContentFilter={relatedContentFilter}
          referralContactCount={referralContactCount}
        />
      </Route>
    </Switch>
  )
}
export default ProvidersCollection
